import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, ArrowRight, Check } from "lucide-react";
import SectionLabel from "@/components/SectionLabel";
import SpeelruimteBanner from "@/components/SpeelruimteBanner";
import { SquiggleUnderline, RouteLine } from "@/components/Squiggle";
import { usePerspective } from "@/context/PerspectiveContext";
import usePageMeta from "@/hooks/usePageMeta";
import { buildPilotMailto } from "@/lib/mailto";

const HERO_IMGS = [
  "https://images.unsplash.com/photo-1523240795612-9a054b0db644?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  "https://images.unsplash.com/photo-1522163182402-834f871fd851?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
  "https://images.unsplash.com/photo-1531123897727-8f129e1688ce?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
  "https://images.unsplash.com/photo-1502680390469-be75c86b636f?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
];

function PerspectiveHero() {
  const { perspective } = usePerspective();
  const HERO_IMGS_LOCAL = HERO_IMGS;

  const isStudent = perspective === "deelnemer";
  const props = isStudent
    ? {
        eyebrow: "LevelLeven Student Reset · voor jou",
        headline: (
          <>
            Loopt het even{" "}
            <span className="italic text-ll-coral relative inline-block">
              niet mee
              <SquiggleUnderline color="#E06B3F" offset={-2} height={18} />
            </span>?
          </>
        ),
        belofte: "Het leven mag ondertussen ook gewoon weer leuk worden — ook naast je studie.",
        subhead:
          "Twaalf weken speelruimte om te ontdekken wat wél werkt. Geen extra vak, geen dossier. Wel een groep, iemand die naast je loopt en een concrete volgende stap.",
        chips: ["12 weken", "max 12 studenten", "2 × 3 uur per week", "In jouw tempo"],
        ctaPrimary: { label: "Kom een keer meelopen", to: "/contact?rol=student", testid: "onderwijs-cta-student" },
        ctaSecondary: { label: "Alles over de Reset", to: "#reset-info" },
        tone: "coral",
      }
    : {
        eyebrow: "LevelLeven Student Reset",
        headline: (
          <>
            Welke studenten krijgt u{" "}
            <span className="italic text-ll-coral relative inline-block">
              ondanks alle
              <SquiggleUnderline color="#E06B3F" offset={-2} height={18} />
            </span>
            <br />
            ondersteuning niet in beweging?
          </>
        ),
        belofte: "De volgende beweging begint klein — meetbaar, in fase.",
        subhead:
          "Voor studenten bij wie verzuim oploopt, motivatie verdwijnt of meerdere problemen tegelijk spelen. Twaalf weken aanvullend op SLB, LOB, decanen en zorgteams — met nul- en eindmeting.",
        chips: ["12 weken", "max 12 studenten", "2 × 3 uur per week", "Nul- en eindmeting", "Warme overdracht"],
        ctaPrimary: {
          label: "Bespreek een pilot",
          href: buildPilotMailto({
            subject: "Bespreek een Student Reset-pilot",
            body: "Beste LevelLeven,\n\nWij willen graag een Student Reset-pilot bespreken.\n\nInstelling:\nContactpersoon:\nFunctie:\n\nMet vriendelijke groet,",
          }),
          testid: "onderwijs-cta-hero-pilot",
        },
        ctaSecondary: { label: "Download brochure", href: "mailto:info@levelleven.nl?subject=Brochure%20Student%20Reset" },
        tone: "sky",
      };

  return (
    <AnimatePresence mode="wait">
      <motion.div
        key={perspective}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.3 }}
      >
        <SpeelruimteBanner {...props} images={HERO_IMGS_LOCAL} />
      </motion.div>
    </AnimatePresence>
  );
}


const belemmeringen = [
  "Gebrek aan richting",
  "Persoonlijke omstandigheden",
  "Verminderde motivatie",
  "Weinig structuur",
  "Financiële druk",
  "Een negatief zelfbeeld",
  "Problemen thuis",
  "Onzekerheid over de toekomst",
];

const vragen = [
  { n: "01", t: "Wat is er gebeurd?", d: "Wat heeft ertoe geleid dat de student is vastgelopen?" },
  { n: "02", t: "Wat houdt beweging tegen?", d: "Welke overtuigingen, omstandigheden, patronen of praktische belemmeringen spelen mee?" },
  { n: "03", t: "Wat kan de student dragen?", d: "Welke belastbaarheid, structuur en ondersteuning zijn op dit moment realistisch?" },
  { n: "04", t: "Waar ligt perspectief?", d: "Welke opleiding, leerwerkroute, vorm van participatie of arbeid past?" },
  { n: "05", t: "Wat is de volgende haalbare stap?", d: "Niet alleen kiezen, maar bewegen." },
];

const doelgroepen = [
  {
    k: "Sophie, 18 · oplopend verzuim",
    p: "Nog ingeschreven, maar steeds minder aanwezig. Motivatie wisselt, contact met mentoren wordt onregelmatig, huiswerk blijft liggen.",
    doel: "Eerder begrijpen wat er speelt en waar mogelijk uitval voorkomen.",
  },
  {
    k: "Naïm, 19 · twee opleidingen afgebroken",
    p: "Geen startkwalificatie en geen duidelijke volgende route. Losstaande werkervaring, maar geen concreet toekomstbeeld.",
    doel: "Voorkomen dat tijdelijke uitval verandert in langdurige stilstand.",
  },
  {
    k: "Youssef, 20 · meerdere problemen tegelijk",
    p: "Motivatie lijkt het probleem, maar thuis, financiën en mentale belasting spelen door elkaar. Losse interventies helpen tijdelijk.",
    doel: "Vanuit zelfinzicht komen tot een realistisch en gedragen vervolgperspectief.",
  },
];

const fasen = [
  {
    n: "01",
    t: "Fundament",
    kop: "Inzicht",
    d: "Wie ben ik? Wat is er gebeurd? Wat vind ik belangrijk? Welke patronen spelen een rol? Waar wil ik naartoe?",
    r: "De student krijgt meer inzicht in zichzelf en de situatie waarin hij of zij is vastgelopen.",
  },
  {
    n: "02",
    t: "Beweging",
    kop: "Eigenaarschap en ontwikkeling",
    d: "Werken aan zelfbeeld, motivatie, keuzes maken, verantwoordelijkheid, structuur, gedrag, communicatie, omgaan met belemmeringen, persoonlijke doelen en belastbaarheid.",
    r: "Van begrijpen wat er gebeurt naar daadwerkelijk ander gedrag kunnen inzetten.",
  },
  {
    n: "03",
    t: "Perspectief",
    kop: "Van ontwikkeling naar een concrete vervolgstap",
    d: "Samen onderzoeken we wat haalbaar en passend is: opleiding voortzetten, overstappen, BBL of andere leerwerkroute, werk, participatie, of aanvullende ondersteuning.",
    r: "Geen standaard vervolgroute, maar een onderbouwde volgende stap.",
  },
  {
    n: "04",
    t: "Overgang en borging",
    kop: "Optioneel",
    d: "Waar gewenst kan Levelleven ook ondersteunen bij de daadwerkelijke overgang naar opleiding of werk. De student wordt niet losgelaten op het moment dat een keuze is gemaakt.",
    r: "We volgen of de gekozen stap ook in de praktijk standhoudt.",
    opt: true,
  },
];

const kpis = {
  Ontwikkeling: [
    "Aanwezigheid en deelname",
    "Ervaren toekomstperspectief",
    "Zelfregie",
    "Motivatie",
    "Dagstructuur",
    "Ervaren belastbaarheid",
    "Eigenaarschap",
    "Duidelijkheid over opleiding en beroep",
    "Vervolgstapgereedheid",
  ],
  Uitkomst: [
    "Behouden voor huidige opleiding",
    "Overstap naar andere opleiding",
    "Terugkeer naar onderwijs",
    "Instroom BBL of leerwerkroute",
    "Uitstroom naar werk",
    "Doorverwijzing naar passende ondersteuning",
    "Uitval / terugval tijdens follow-up",
  ],
  Organisatie: [
    "Bereik van de doelgroep",
    "Deelnamepercentage",
    "Programmabehoud",
    "Redenen voor uitval",
    "Doorlooptijd",
    "Type vervolgroute",
    "Ervaringen van student en verwijzer",
  ],
};

const fadeUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-15%" },
  transition: { duration: 0.9, ease: [0.22, 1, 0.36, 1] },
};

export default function Onderwijs() {
  usePageMeta({
    title: "Voor onderwijs, LevelLeven",
    description:
      "Ontwikkelroute voor mbo- en hbo-studenten die dreigen vast te lopen, uitgevallen zijn of moeten heroriënteren. Aanvullend op SLB, LOB, decanen, zorgteams en Doorstroompunten.",
  });

  return (
    <div className="bg-ll-cream text-ll-ink">
      {/* HERO — perspective-aware */}
      <PerspectiveHero />

      {/* AANSLUITING OP BESTAANDE BEGELEIDING */}
      <section className="py-24 sm:py-32 bg-[#F0EAD8]">
        <div className="max-w-[1100px] mx-auto px-6 sm:px-10 lg:px-16">
          <motion.h2
            {...fadeUp}
            className="font-heading text-3xl sm:text-4xl lg:text-5xl leading-tight tracking-tight text-[#1F5132] font-light"
          >
            Geen nieuwe voorziening naast{" "}
            <span className="italic text-[#E06B3F]">uw bestaande ondersteuning</span>.
          </motion.h2>
          <motion.p
            {...fadeUp}
            transition={{ ...fadeUp.transition, delay: 0.15 }}
            className="mt-8 text-lg text-[#1F5132] leading-relaxed font-light max-w-3xl"
          >
            LevelLeven Student Reset sluit aan op mentorbegeleiding, SLB, LOB,
            studentondersteuning, zorgteams en Doorstroompunt. We richten ons
            op studenten die tijdelijk onvoldoende in staat zijn om van dat
            bestaande aanbod gebruik te maken.
          </motion.p>
        </div>
      </section>

      {/* VRAAGSTUK (was: Het vraagstuk uit vorige versie) */}
      <section className="py-24 sm:py-32" data-testid="onderwijs-studenten">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
          <div className="max-w-3xl mb-14">
            <SectionLabel>Welke studenten</SectionLabel>
            <motion.h2
              {...fadeUp}
              className="font-heading text-4xl sm:text-5xl lg:text-6xl leading-tight tracking-tight text-[#1F5132] font-light"
            >
              Herkent u{" "}
              <span className="italic text-[#E06B3F]">deze studenten</span> op uw instelling?
            </motion.h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              {
                initial: "S",
                naam: "Sophie, 18",
                label: "Oplopend verzuim",
                body: "Nog ingeschreven, maar steeds minder aanwezig. Motivatie wisselt, contact met mentoren wordt onregelmatig.",
                accent: "#D19B72",
              },
              {
                initial: "N",
                naam: "Naïm, 19",
                label: "Twee opleidingen afgebroken",
                body: "Geen startkwalificatie en geen duidelijke volgende route. Losse werkervaring, geen concreet toekomstbeeld.",
                accent: "#B8896C",
              },
              {
                initial: "Y",
                naam: "Youssef, 20",
                label: "Meerdere problemen tegelijk",
                body: "Motivatie lijkt het probleem, maar thuis, financiën en mentale belasting spelen door elkaar.",
                accent: "#8B5A4E",
              },
              {
                initial: "L",
                naam: "Lisa, 21",
                label: "Studieheroriëntatie",
                body: "Begon vol motivatie aan hbo, houdt het tempo en de inhoud niet meer vast. Twijfelt over studiekeuze.",
                accent: "#6E7F6E",
              },
            ].map((c) => (
              <div
                key={c.naam}
                className="bg-[#FAF7F1] border border-[#1F5132]/10 rounded-2xl p-6"
              >
                <div
                  className="w-16 h-16 rounded-full flex items-center justify-center mb-5"
                  style={{ backgroundColor: `${c.accent}1A` }}
                >
                  <span
                    className="chapter-num text-4xl leading-none"
                    style={{ color: c.accent }}
                  >
                    {c.initial}
                  </span>
                </div>
                <p
                  className="text-[10px] uppercase tracking-[0.3em] mb-2"
                  style={{ color: c.accent }}
                >
                  {c.label}
                </p>
                <p className="font-heading text-xl text-[#1F5132] leading-snug">
                  {c.naam}
                </p>
                <p className="mt-3 text-sm text-[#1F5132] leading-relaxed">
                  {c.body}
                </p>
              </div>
            ))}
          </div>
          <p className="mt-8 text-xs text-[#3E4A42] italic max-w-3xl">
            Samengestelde casussen, gebaseerd op kenmerken van de beoogde
            doelgroep. Het zijn geen bestaande studenten.
          </p>
        </div>
      </section>

      {/* AANPAK 5 TUSSENKOPPEN */}
      <section
        className="py-24 sm:py-32 bg-[#F0EAD8]"
        data-testid="onderwijs-aanpak"
      >
        <div className="max-w-[1100px] mx-auto px-6 sm:px-10 lg:px-16">
          <SectionLabel>De aanpak</SectionLabel>
          <motion.h2
            {...fadeUp}
            className="font-heading text-4xl sm:text-5xl lg:text-6xl leading-tight tracking-tight text-[#1F5132] font-light mb-14"
          >
            Zo werkt de{" "}
            <span className="italic text-[#E06B3F]">Student Reset</span>.
          </motion.h2>

          <ol className="divide-y divide-[#1F5132]/10 border-t border-b border-[#1F5132]/10">
            {[
              {
                n: "01",
                t: "Voor wie?",
                d: "Studenten die dreigen uit te vallen, langdurig verzuimen, vastlopen ondanks begeleiding, recent zijn uitgevallen of geen gedragen vervolgrichting hebben.",
              },
              {
                n: "02",
                t: "Niet: hoe krijgen we hem terug in de les?",
                d: "Maar: wat heeft deze student nodig om weer duurzaam te kunnen leren, kiezen en meedoen?",
              },
              {
                n: "03",
                t: "Eén cohort. Twaalf weken.",
                d: "Maximaal twaalf studenten. Twee groepsmomenten van drie uur per week plus individuele begeleiding.",
              },
              {
                n: "04",
                t: "We meten wat verandert.",
                d: "Vertrekpunt, aanwezigheid, structuur, motivatie en regie, perspectief en concrete vervolgstap.",
              },
              {
                n: "05",
                t: "Na twaalf weken verder.",
                d: "Huidige opleiding, andere opleiding, BBL, stage, werk of passende ondersteuning.",
              },
            ].map((s, i) => (
              <motion.li
                key={s.n}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-10%" }}
                transition={{ duration: 0.7, delay: i * 0.05 }}
                className="grid grid-cols-1 md:grid-cols-12 gap-6 py-8"
              >
                <span className="md:col-span-2 chapter-num text-4xl md:text-5xl text-[#E06B3F] leading-none tabular-nums">
                  {s.n}
                </span>
                <p className="md:col-span-4 font-heading text-2xl md:text-3xl text-[#1F5132] leading-snug font-light">
                  {s.t}
                </p>
                <p className="md:col-span-6 text-[#1F5132] font-light leading-relaxed">
                  {s.d}
                </p>
              </motion.li>
            ))}
          </ol>

          <motion.p
            {...fadeUp}
            transition={{ ...fadeUp.transition, delay: 0.2 }}
            className="mt-16 font-heading text-2xl sm:text-3xl lg:text-4xl leading-snug tracking-tight text-[#1F5132] font-light max-w-3xl border-l-4 border-[#E06B3F] pl-6"
          >
            Ons uitgangspunt is dat een student na twaalf weken{" "}
            <span className="italic text-[#E06B3F]">
              weer verder kan binnen het bestaande onderwijs- en
              ondersteuningslandschap
            </span>. Alleen wanneer langere begeleiding aantoonbaar meerwaarde
            heeft, kan een vervolg worden ingericht.
          </motion.p>
        </div>
      </section>

      {/* VRAAGSTUK LEGACY */}
      <section className="hidden">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-12 items-start">
            <div className="md:col-span-5">
              <SectionLabel>Het vraagstuk</SectionLabel>
              <motion.h2
                {...fadeUp}
                className="font-heading text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-[#1F5132] font-light"
              >
                Uitval begint{" "}
                <span className="italic text-[#E06B3F]">niet altijd</span> bij
                de opleiding.
              </motion.h2>
            </div>
            <motion.div
              {...fadeUp}
              transition={{ ...fadeUp.transition, delay: 0.15 }}
              className="md:col-span-6 md:col-start-7 space-y-6 text-lg text-[#1F5132] font-light leading-relaxed"
            >
              <p>
                Een verkeerde studiekeuze kan een rol spelen. Maar achter
                uitval kunnen ook andere factoren zitten. Vaak versterken ze
                elkaar.
              </p>
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-4">
                {belemmeringen.map((b) => (
                  <li
                    key={b}
                    className="flex items-start gap-3 text-[#1F5132] leading-snug"
                  >
                    <span className="mt-2 w-1.5 h-1.5 rounded-full bg-[#E06B3F] shrink-0" />
                    {b}
                  </li>
                ))}
              </ul>
              <p className="mt-6">
                Dan is alleen een gesprek over aanwezigheid, studievoortgang
                of de volgende opleiding niet altijd genoeg. Als niet
                duidelijk is waarom iemand vastloopt, is het moeilijk om te
                bepalen welke volgende stap duurzaam past.
              </p>
              <p className="text-[#1F5132]">
                LevelLeven vervangt SLB, LOB, studentpsychologen, decanen,
                zorgteams, Doorstroompunten of bestaande begeleiding niet.
                Het is een aanvullende ontwikkelroute voor studenten bij wie
                méér nodig is om weer beweging te creëren.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* OMVANG + CIJFERS */}
      <section className="py-24 sm:py-32 bg-[#F0EAD8]">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
          <div className="max-w-3xl mb-14">
            <SectionLabel>De omvang van het vraagstuk</SectionLabel>
            <motion.h2
              {...fadeUp}
              className="font-heading text-4xl sm:text-5xl leading-[1.05] tracking-tight text-[#1F5132] font-light"
            >
              Duizenden jongeren verliezen ieder jaar{" "}
              <span className="italic text-[#E06B3F]">de aansluiting</span>{" "}
              met onderwijs.
            </motion.h2>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <motion.article
              {...fadeUp}
              transition={{ ...fadeUp.transition, delay: 0.1 }}
              className="bg-[#FAF7F1] border border-[#1F5132]/10 rounded-2xl p-8 sm:p-10"
            >
              <p className="text-xs uppercase tracking-[0.3em] text-[#E06B3F] mb-4">
                Mbo · schooljaar 2024–2025
              </p>
              <p className="chapter-num text-6xl sm:text-7xl text-[#1F5132] leading-none">
                21.747
              </p>
              <p className="mt-4 font-heading text-xl text-[#1F5132] font-light leading-snug">
                Nieuwe voortijdig schoolverlaters
              </p>
              <p className="mt-3 text-sm text-[#1F5132] leading-relaxed">
                Op een startpopulatie van 372.405 mbo-studenten.¹
              </p>
            </motion.article>

            <motion.article
              {...fadeUp}
              transition={{ ...fadeUp.transition, delay: 0.2 }}
              className="bg-[#FAF7F1] border border-[#1F5132]/10 rounded-2xl p-8 sm:p-10"
            >
              <p className="text-xs uppercase tracking-[0.3em] text-[#E06B3F] mb-4">
                Hbo · cohort 2023
              </p>
              <p className="chapter-num text-6xl sm:text-7xl text-[#1F5132] leading-none">
                12,2 – 16,3%
              </p>
              <p className="mt-4 font-heading text-xl text-[#1F5132] font-light leading-snug">
                Niet meer ingeschreven na één jaar
              </p>
              <p className="mt-3 text-sm text-[#1F5132] leading-relaxed">
                Vrouwelijke resp. mannelijke studenten. Percentages
                verschillen sterk naar vooropleiding.²
              </p>
            </motion.article>
          </div>

          <p className="mt-10 max-w-4xl text-sm text-[#1F5132] leading-relaxed">
            Mbo-vsv en hbo-uitval zijn niet hetzelfde. Een mbo-vsv'er verlaat
            het onderwijs zonder startkwalificatie; een hbo-student die stopt
            kan bijvoorbeeld overstappen, later terugkeren of al over een
            startkwalificatie beschikken. Maar achter beide cijfers ligt
            dezelfde belangrijke vraag: wat gebeurt er met de jongere ná het
            moment waarop de opleiding niet meer verdergaat?
          </p>
        </div>
      </section>

      {/* VIJF VRAGEN */}
      <section className="py-24 sm:py-32">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
          <div className="max-w-3xl mb-14">
            <SectionLabel>Niet iedere student heeft meer onderwijs nodig</SectionLabel>
            <motion.h2
              {...fadeUp}
              className="font-heading text-4xl sm:text-5xl leading-[1.05] tracking-tight text-[#1F5132] font-light"
            >
              Soms is eerst{" "}
              <span className="italic text-[#E06B3F]">iets anders</span>{" "}
              nodig.
            </motion.h2>
            <p className="mt-6 text-lg text-[#1F5132] leading-relaxed font-light">
              Wanneer een student vastloopt, ligt de reflex vaak bij een
              andere opleiding, extra studiebegeleiding, een studiekeuzetest
              of zo snel mogelijk richting werk. Dat kan de juiste oplossing
              zijn. Maar niet voor iedere student. LevelLeven begint daarom
              één stap eerder.
            </p>
          </div>

          <ul className="grid grid-cols-1 md:grid-cols-12 gap-4">
            {vragen.map((v) => (
              <motion.li
                key={v.n}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-10%" }}
                transition={{ duration: 0.7, delay: parseInt(v.n) * 0.05, ease: [0.22, 1, 0.36, 1] }}
                className="md:col-span-4 lg:col-span-4 bg-[#FAF7F1] border border-[#1F5132]/10 rounded-2xl p-7"
              >
                <p className="chapter-num text-3xl text-[#E06B3F] leading-none tabular-nums mb-4">
                  {v.n}
                </p>
                <p className="font-heading text-2xl text-[#1F5132] leading-snug font-light">
                  {v.t}
                </p>
                <p className="mt-3 text-sm text-[#1F5132] leading-relaxed">
                  {v.d}
                </p>
              </motion.li>
            ))}
          </ul>
        </div>
      </section>

      {/* DOELGROEPEN */}
      <section className="py-24 sm:py-32 bg-[#F0EAD8]" data-testid="onderwijs-doelgroepen">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
          <div className="max-w-3xl mb-14">
            <SectionLabel>Voor wie</SectionLabel>
            <motion.h2
              {...fadeUp}
              className="font-heading text-4xl sm:text-5xl leading-[1.05] tracking-tight text-[#1F5132] font-light"
            >
              Drie momenten waarop LevelLeven kan{" "}
              <span className="italic text-[#E06B3F]">worden ingezet</span>.
            </motion.h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {doelgroepen.map((d, i) => (
              <motion.article
                key={d.k}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-10%" }}
                transition={{ duration: 0.7, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] }}
                className="bg-[#FAF7F1] border border-[#1F5132]/10 rounded-2xl p-8"
              >
                <p className="text-[10px] uppercase tracking-[0.35em] text-[#E06B3F] mb-6">
                  0{i + 1}
                </p>
                <p className="font-heading text-2xl text-[#1F5132] leading-tight font-light">
                  {d.k}
                </p>
                <p className="mt-4 text-[#1F5132] leading-relaxed">{d.p}</p>
                <div className="mt-6 pt-4 border-t border-[#1F5132]/10">
                  <p className="text-[10px] uppercase tracking-[0.25em] text-[#3E4A42] mb-1">
                    Doel
                  </p>
                  <p className="text-sm text-[#1F5132] font-medium">{d.doel}</p>
                </div>
              </motion.article>
            ))}
          </div>
        </div>
      </section>

      {/* PROGRAMMA / FASEN */}
      <section id="programma" className="py-24 sm:py-32">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
          <div className="max-w-3xl mb-14">
            <SectionLabel>Het programma</SectionLabel>
            <motion.h2
              {...fadeUp}
              className="font-heading text-4xl sm:text-5xl leading-[1.05] tracking-tight text-[#1F5132] font-light"
            >
              Eén methodiek. Een persoonlijke route.{" "}
              <span className="italic text-[#E06B3F]">
                Van vastlopen naar opnieuw richting kunnen kiezen.
              </span>
            </motion.h2>
          </div>

          <ol className="divide-y divide-[#1F5132]/10 border-t border-b border-[#1F5132]/10">
            {fasen.map((f, i) => (
              <motion.li
                key={f.n}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-10%" }}
                transition={{ duration: 0.7, delay: i * 0.05, ease: [0.22, 1, 0.36, 1] }}
                className="grid grid-cols-1 md:grid-cols-12 gap-6 py-10"
              >
                <span className="md:col-span-2 chapter-num text-4xl md:text-5xl text-[#E06B3F] leading-none tabular-nums">
                  {f.n}
                </span>
                <div className="md:col-span-3">
                  <p className="font-heading text-2xl md:text-3xl text-[#1F5132] leading-snug font-light">
                    Fase {f.t}
                    {f.opt && (
                      <span className="ml-3 text-[10px] uppercase tracking-[0.3em] text-[#E06B3F] bg-[#E06B3F]/10 rounded-full px-2 py-1 align-middle">
                        Optioneel
                      </span>
                    )}
                  </p>
                  <p className="mt-1 text-xs uppercase tracking-[0.25em] text-[#3E4A42]">
                    {f.kop}
                  </p>
                </div>
                <div className="md:col-span-7 space-y-3">
                  <p className="text-[#1F5132] font-light leading-relaxed">
                    {f.d}
                  </p>
                  <p className="text-sm text-[#1F5132]">
                    <span className="text-[10px] uppercase tracking-[0.25em] text-[#E06B3F] mr-2">
                      Resultaat
                    </span>
                    {f.r}
                  </p>
                </div>
              </motion.li>
            ))}
          </ol>

          {/* SCHEMA */}
          <div className="mt-16 bg-[#F0EAD8] rounded-2xl p-8 sm:p-10">
            <p className="text-xs uppercase tracking-[0.3em] text-[#E06B3F] mb-6">
              Positie in de keten
            </p>
            <div className="flex flex-col md:flex-row gap-4 md:items-center">
              {["School / opleiding", "SLB · LOB · zorgteam · decaan · Doorstroompunt", "LevelLeven", "Vervolg: opleiding, BBL, werk, participatie"].map(
                (s, i, arr) => (
                  <div key={s} className="flex md:flex-1 items-center gap-3">
                    <div className={`flex-1 rounded-2xl p-5 ${i === 2 ? "bg-[#1F5132] text-[#FAF7F1]" : "bg-[#FAF7F1] border border-[#1F5132]/10 text-[#1F5132]"}`}>
                      <p className={`text-[10px] uppercase tracking-[0.25em] mb-1 ${i === 2 ? "text-[#D19B72]" : "text-[#3E4A42]"}`}>
                        Stap {i + 1}
                      </p>
                      <p className="font-heading text-base leading-snug">{s}</p>
                    </div>
                    {i < arr.length - 1 && (
                      <ArrowRight size={16} className="text-[#E06B3F] hidden md:block shrink-0" />
                    )}
                  </div>
                ),
              )}
            </div>
            <p className="mt-6 text-xs text-[#3E4A42] italic">
              Geen parallel systeem. Een aanvullende schakel.
            </p>
          </div>
        </div>
      </section>

      {/* WAT MAKEN WE MEETBAAR */}
      <section className="py-24 sm:py-32 bg-[#1F5132] text-[#FAF7F1]">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
          <div className="max-w-3xl mb-14">
            <p className="text-xs uppercase tracking-[0.3em] text-[#D19B72] mb-6">
              Wat maken we meetbaar
            </p>
            <motion.h2
              {...fadeUp}
              className="font-heading text-4xl sm:text-5xl leading-[1.05] tracking-tight font-light"
            >
              Geen belofte op uitvalreductie.{" "}
              <span className="italic text-[#D19B72]">
                Wel zicht op ontwikkeling en resultaat.
              </span>
            </motion.h2>
            <p className="mt-6 text-lg text-[#FAF7F1]/75 leading-relaxed font-light">
              Iedere pilot start met een nulmeting. Gedurende het programma
              volgen we de ontwikkeling van deelnemers en rapporteren we op
              vooraf afgesproken indicatoren.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {Object.entries(kpis).map(([groep, items]) => (
              <div
                key={groep}
                className="bg-[#FAF7F1]/5 border border-[#FAF7F1]/15 rounded-2xl p-7"
              >
                <p className="text-[10px] uppercase tracking-[0.35em] text-[#D19B72] mb-5">
                  {groep}-KPI's
                </p>
                <ul className="space-y-3">
                  {items.map((k) => (
                    <li
                      key={k}
                      className="flex items-start gap-3 text-sm text-[#FAF7F1]/85 leading-snug"
                    >
                      <Check size={12} className="text-[#D19B72] mt-1 shrink-0" />
                      <span>{k}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          <p className="mt-10 max-w-3xl text-lg font-heading font-light leading-snug">
            Niet alleen weten óf iemand uitvalt. Begrijpen waarom, en wat
            daarna werkt.
          </p>
        </div>
      </section>

      {/* PILOTOPZET */}
      <section className="py-24 sm:py-32" data-testid="onderwijs-pilot">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
          <div className="max-w-3xl mb-14">
            <SectionLabel>De LevelLeven Onderwijspilot</SectionLabel>
            <motion.h2
              {...fadeUp}
              className="font-heading text-4xl sm:text-5xl leading-[1.05] tracking-tight text-[#1F5132] font-light"
            >
              Start klein.{" "}
              <span className="italic text-[#E06B3F]">
                Meet zorgvuldig. Schaal op wat werkt.
              </span>
            </motion.h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              { t: "15–20 studenten", d: "Eén vooraf afgesproken doelgroep, bijvoorbeeld dreigende uitval of recent uitgevallen." },
              { t: "6–9 maanden", d: "Intake, programma, metingen, uitstroombegeleiding en evaluatie." },
              { t: "Eén projectteam", d: "Vaste contactpersoon vanuit de instelling, samenwerking met LOB, SLB, zorgteam en Doorstroompunt." },
              { t: "Nulmeting + eindmeting", d: "Ontwikkeling vanaf het begin meetbaar." },
              { t: "Managementrapportage", d: "Geen individuele dossiers op bestuurstafel, wel inzicht in patronen en vervolgroutes." },
              { t: "Follow-up", d: "Enkele maanden later: staat de student nog in opleiding, ontwikkeling of werk?" },
            ].map((k, i) => (
              <motion.div
                key={k.t}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-10%" }}
                transition={{ duration: 0.7, delay: i * 0.05, ease: [0.22, 1, 0.36, 1] }}
                className="bg-[#FAF7F1] border border-[#1F5132]/10 rounded-2xl p-6"
              >
                <Check size={14} className="text-[#E06B3F] mb-4" />
                <p className="font-heading text-xl text-[#1F5132] leading-tight font-light">
                  {k.t}
                </p>
                <p className="mt-2 text-sm text-[#1F5132] leading-relaxed">
                  {k.d}
                </p>
              </motion.div>
            ))}
          </div>

          {/* PRIJS */}
          <div className="mt-16 grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="bg-[#1F5132] text-[#FAF7F1] rounded-2xl p-8 sm:p-10" data-testid="onderwijs-prijs-ontwikkeling">
              <p className="text-xs uppercase tracking-[0.3em] text-[#D19B72] mb-3">
                Hoofdproduct · Student Reset
              </p>
              <p className="font-heading text-3xl leading-tight font-light">
                Vanaf circa <span className="text-[#D19B72]">€ 45.000</span>{" "}
                per cohort van 12 studenten.
              </p>
              <p className="mt-4 text-sm text-[#FAF7F1]/80 leading-relaxed">
                Voor studenten die nú vastlopen, afglijden, verzuimen of
                dreigen uit te vallen. Twaalf weken intensieve herstart met
                nul- en eindmeting en een concrete vervolgstap.
              </p>
              <p className="mt-4 text-xs text-[#FAF7F1]/70">
                12 weken · max 12 studenten · 2 × 3 uur per week · individueel
                waar nodig · warme overdracht
              </p>
            </div>

            <div className="bg-[#FAF7F1] border border-[#1F5132]/10 rounded-2xl p-8 sm:p-10" data-testid="onderwijs-prijs-doorstroom">
              <p className="text-xs uppercase tracking-[0.3em] text-[#3E4A42] mb-3">
                Vervolgmogelijkheid · LevelLeven Continu
              </p>
              <p className="font-heading text-2xl text-[#1F5132] leading-snug font-light">
                Alleen wanneer verder ondersteunen aantoonbaar meerwaarde heeft
              </p>
              <p className="mt-4 text-sm text-[#1F5132] leading-relaxed">
                Na de Reset zijn er drie routes: (A) student kan zelfstandig
                verder, (B) warme overdracht naar bestaande voorziening,
                (C) langere begeleiding via Continu wanneer dat aantoonbaar
                helpt om de beweging vast te houden.
              </p>
              <p className="mt-4 text-xs text-[#3E4A42] italic">
                Continu wordt niet vooraf ingekocht. Het wordt pas ingericht
                op basis van de bevindingen uit de Reset. Prijs op maat.
              </p>
            </div>
          </div>

          <p className="mt-8 max-w-3xl text-sm text-[#1F5132] leading-relaxed italic">
            Een pilot is geen kostenpost zonder leeropbrengst. Een goede
            pilot moet antwoord geven op drie vragen: bereiken we de
            studenten die met bestaande begeleiding onvoldoende verder
            komen? Ontstaat er aantoonbare ontwikkeling? Leidt die
            ontwikkeling tot een duurzame vervolgstap? Pas daarna ontstaat
            een verantwoorde basis voor opschaling.
          </p>
        </div>
      </section>

      {/* MOMENTUM + CPB */}
      <section className="py-24 sm:py-32 bg-[#F0EAD8]">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
            <div className="md:col-span-5">
              <SectionLabel>Momentum</SectionLabel>
              <motion.h2
                {...fadeUp}
                className="font-heading text-4xl sm:text-5xl leading-[1.05] tracking-tight text-[#1F5132] font-light"
              >
                Er is momentum om{" "}
                <span className="italic text-[#E06B3F]">
                  anders naar uitval
                </span>{" "}
                te kijken.
              </motion.h2>
            </div>
            <motion.div
              {...fadeUp}
              transition={{ ...fadeUp.transition, delay: 0.15 }}
              className="md:col-span-7 space-y-6 text-[#1F5132] font-light leading-relaxed"
            >
              <p>
                Vanaf 2026 verplicht de Wet Van school naar duurzaam werk
                mbo-instellingen, gemeenten en Doorstroompunten intensiever
                samen te werken om jongeren beter te begeleiden van school
                naar vervolgonderwijs of duurzaam werk. Voor de uitvoering
                is vanaf 2026 €196 miljoen beschikbaar, waarvan €94 miljoen
                voor regionale programma's.³
              </p>
              <p>
                Voor mbo-instellingen betekent dit dat preventie,
                begeleiding, samenwerking en duurzame vervolgroutes
                nadrukkelijker onderdeel worden van de regionale opgave.
                Voor hogescholen ligt de aansluiting vooral bij
                studiesucces, studentwelzijn en heroriëntatie.
              </p>
              <div className="mt-6 p-6 bg-[#FAF7F1] border-l-4 border-[#E06B3F] rounded-r-xl">
                <p className="text-[10px] uppercase tracking-[0.3em] text-[#E06B3F] mb-3">
                  Wat de wetenschap laat zien
                </p>
                <p className="text-[#1F5132] leading-relaxed">
                  Een gerandomiseerd CPB-onderzoek onder mbo-studenten liet
                  zien dat één jaar intensieve coaching de kans op
                  voortijdig schoolverlaten in de onderzochte groep
                  verminderde van ongeveer 17% naar 10%. De maatschappelijke
                  baten waren naar verwachting groter dan de kosten.⁴
                </p>
                <p className="mt-3 text-xs text-[#3E4A42] italic">
                  Dat onderzoek bewijst niet dat LevelLeven hetzelfde effect
                  zal bereiken. Het laat wel iets belangrijks zien: intensieve,
                  persoonlijke begeleiding van studenten met een verhoogd
                  uitvalrisico kan meetbaar verschil maken. Daarom willen we
                  het effect binnen onderwijs niet veronderstellen. We willen
                  het meten.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* VOOR HET COLLEGE VAN BESTUUR */}
      <section className="py-32 sm:py-40 bg-[#1F5132] text-[#FAF7F1]">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
          <p className="text-xs uppercase tracking-[0.3em] text-[#D19B72] mb-8">
            Voor het College van Bestuur
          </p>
          <motion.h2
            {...fadeUp}
            className="font-heading text-4xl sm:text-5xl lg:text-6xl xl:text-7xl leading-[1.05] tracking-tight font-light max-w-4xl"
          >
            Wat als we{" "}
            <span className="italic text-[#D19B72]">
              eerder kunnen zien
            </span>{" "}
            wat achter uitval zit?
          </motion.h2>
          <div className="mt-10 max-w-3xl space-y-6 text-lg text-[#FAF7F1]/80 leading-relaxed font-light">
            <p>
              Iedere onderwijsinstelling kent studenten voor wie de
              bestaande begeleiding niet voldoende beweging oplevert.
              Sommigen verdwijnen langzaam uit beeld. Sommigen schrijven
              zich uit. Sommigen beginnen opnieuw en vallen opnieuw uit.
            </p>
            <p>
              LevelLeven wil daarin een aanvullende rol vervullen. Niet
              door bestaande professionals te vervangen. Niet door een
              nieuwe laag bureaucratie toe te voegen. En niet door vooraf
              rendement te beloven dat nog niet is aangetoond.
            </p>
            <p className="text-[#FAF7F1]">
              Selecteer samen met ons 15 tot 20 studenten voor wie de
              bestaande aanpak onvoldoende beweging oplevert. Geef ons één
              pilotperiode. Meet het vertrekpunt. Volg de ontwikkeling.
              Leg vast waar deelnemers terechtkomen. Bepaal daarna samen op
              basis van de resultaten of deze aanpak waarde toevoegt.
            </p>
            <p className="font-heading text-2xl text-[#D19B72] italic">
              Klein beginnen. Bewijs verzamelen. Opschalen wat werkt.
            </p>
          </div>

          <div className="mt-14">
            <a
              href={buildPilotMailto({
                subject: "Bespreek een onderwijspilot met LevelLeven",
                body:
                  "Beste LevelLeven,\n\nWij willen graag een onderwijspilot bespreken.\n\nInstelling:\nContactpersoon:\nFunctie:\nTelefoon:\n\nMet vriendelijke groet,",
              })}
              data-testid="onderwijs-cta-cvb"
              className="group inline-flex items-center gap-4 bg-[#E06B3F] hover:bg-[#FAF7F1] hover:text-[#1F5132] rounded-full pl-7 pr-2 py-2 transition-colors duration-300"
            >
              <span className="text-base font-medium">
                Bespreek een pilot met LevelLeven
              </span>
              <span className="bg-[#FAF7F1] text-[#1F5132] group-hover:bg-[#E06B3F] group-hover:text-white w-11 h-11 rounded-full flex items-center justify-center transition-colors">
                <ArrowUpRight size={18} />
              </span>
            </a>
          </div>
        </div>
      </section>

      {/* SLOTVRAAG */}
      <section className="py-24 sm:py-32">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
          <p className="text-xs uppercase tracking-[0.3em] text-[#E06B3F] mb-8">
            De vraag die wij onderwijsinstellingen stellen
          </p>
          <motion.h2
            {...fadeUp}
            className="font-heading text-3xl sm:text-4xl lg:text-5xl leading-[1.1] tracking-tight text-[#1F5132] font-light max-w-5xl"
          >
            Als u over negen maanden niet alleen weet hoeveel studenten
            zijn uitgevallen, maar ook{" "}
            <span className="italic text-[#E06B3F]">
              wat hen tegenhield, welke ontwikkeling zij hebben doorgemaakt
              en welke vervolgstap duurzaam bij hen past
            </span>{" "}
            — wat zou dat betekenen voor uw aanpak van studiesucces en
            uitval?
          </motion.h2>
          <div className="mt-10">
            <Link
              to="/contact?intent=onderwijs-kennismaking"
              data-testid="onderwijs-cta-kennismaking"
              className="group inline-flex items-center gap-4 bg-[#1F5132] hover:bg-[#E06B3F] text-[#FAF7F1] rounded-full pl-7 pr-2 py-2 transition-colors duration-300"
            >
              <span className="text-base font-medium">
                Plan een kennismakingsgesprek
              </span>
              <span className="bg-[#E06B3F] group-hover:bg-[#1F5132] text-white w-11 h-11 rounded-full flex items-center justify-center transition-colors">
                <ArrowUpRight size={18} />
              </span>
            </Link>
          </div>
        </div>
      </section>

      {/* BRONNEN */}
      <section className="py-16 border-t border-[#1F5132]/10">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
          <p className="text-xs uppercase tracking-[0.3em] text-[#E06B3F] mb-6">
            Bronnen
          </p>
          <ol className="space-y-4 max-w-4xl text-sm text-[#1F5132] leading-relaxed">
            <li className="flex gap-4">
              <span className="text-[#3E4A42]">1</span>
              <span>
                Ministerie van Onderwijs, Cultuur en Wetenschap. (2026).{" "}
                <em>Aantal nieuwe vsv'ers per jaar (vanuit het mbo)</em>. OCW
                in Cijfers.
              </span>
            </li>
            <li className="flex gap-4">
              <span className="text-[#3E4A42]">2</span>
              <span>
                Vereniging Hogescholen. (2025).{" "}
                <em>Factsheet Studiesucces, uitval en studiewissel 2023/2024</em>.
              </span>
            </li>
            <li className="flex gap-4">
              <span className="text-[#3E4A42]">3</span>
              <span>
                Rijksoverheid. (2026). <em>Aanpak voortijdig schoolverlaten (vsv)</em>.
                Geraadpleegd september 2026.
              </span>
            </li>
            <li className="flex gap-4">
              <span className="text-[#3E4A42]">4</span>
              <span>
                Van der Steeg, M., Van Elk, R., & Webbink, D. (2012).{" "}
                <em>
                  Zorgt intensieve coaching voor minder voortijdig
                  schoolverlaten? Empirisch bewijs van een gerandomiseerd
                  experiment
                </em>
                . Centraal Planbureau.
              </span>
            </li>
          </ol>
        </div>
      </section>
    </div>
  );
}
